/** Where the ledger sits next to the record's panels (plan §1.2, §1.6).
 *
 *  - Content 1100 px wide or more: a sticky 340 px column (`width`: a dock with a preview
 *    is `PREVIEW_DOCK_WIDTH`, 380) on the right with its own scroll; it slides in from the
 *    right when edit mode opens (200 ms, none under reduced motion) while the panels
 *    column narrows. 1100, not the plan's 1200: a 1440 px
 *    laptop with the nav drawer open leaves ~1152 px of content, and that operator gets
 *    the docked ledger (coordinator decision, delegated by the user 2026-10-01). The
 *    column ends 16 px above the viewport bottom wherever it sits — under the header at
 *    the top of the page as well as stuck under the spine — so its foot (Save) is always
 *    in view.
 *  - Narrower content: the panels take the full width and the ledger becomes a bottom
 *    sheet — fixed to the viewport bottom over the content column, collapsed to a peek
 *    bar ("Draft · 4 changes · Save") until opened. Fixed, not sticky: a sticky sheet
 *    cannot rise above the dock's top, so near the top of the page the open sheet's
 *    foot (Save) would hang below the viewport.
 *    The page scrolls clear of it at its END — the app scroller ends in a spacer as tall
 *    as the sheet (`useBottomBarClearance`), so nothing after the dock (a page's footer)
 *    ends under the sheet either.
 *
 *  `dockInView`: the side column stays in view mode too (the tour's Preview | Changes
 *  dock, D6) — the ledger renders whether or not `open`, so pressing E changes what it
 *  shows, never the layout; the bottom sheet still appears only in edit mode. The slide-in
 *  says "the editor opened", so a column that appears with the page (or comes back from a
 *  tab without it) does not slide.
 *
 *  The box measured is the dock's own (the page content, whatever the nav rail takes).
 *  Both layouts keep the ledger at the same place in the tree, so switching between
 *  them never remounts it — its receipt and save state survive a resize.
 *
 *      <LedgerDock open={editing} stickyTop={offset} ledger={(variant) => <Ledger variant={variant} />}>
 *        {panels}
 *      </LedgerDock>
 */
import { Box, keyframes } from '@mui/material';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { useBottomBarClearance } from '@/layout/AppScroller';

import type { LedgerVariant } from './Ledger';

export const LEDGER_WIDTH = 340;
/** Between the panels and the side column. */
export const LEDGER_GAP_PX = 16;
/** Content narrower than this gets the bottom sheet; at or above it the ledger docks. */
export const SHEET_BELOW_PX = 1100;
/** Between the side column's foot and the viewport bottom. */
const SIDE_BOTTOM_GAP_PX = 16;
/** The custom property holding the side column's top in the viewport (px). */
const SIDE_TOP_VAR = '--ledger-top';

const slideIn = keyframes`
  from { transform: translateX(16px); opacity: 0.4; }
  to { transform: none; opacity: 1; }
`;
const slideUp = keyframes`
  from { transform: translateY(16px); opacity: 0.4; }
  to { transform: none; opacity: 1; }
`;

const MOTION = {
  animationDuration: '200ms',
  animationTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

export interface LedgerDockProps {
  /** Edit mode — the ledger is shown. */
  open: boolean;
  ledger: (variant: LedgerVariant) => ReactNode;
  children: ReactNode;
  /** Sticky offset of the side column below the page's sticky chrome (px). */
  stickyTop?: number;
  /** Viewport px above the scroll container's content edge (app bar + its padding), so
   *  the side column's height fits the viewport. Default 0: the document scrolls. */
  viewportOffset?: number;
  /** `auto` measures; tests and callers may pin one. */
  layout?: 'auto' | LedgerVariant;
  /** The side column's width (px). Default `LEDGER_WIDTH` (340); a dock with a preview is
   *  `PREVIEW_DOCK_WIDTH` (380). */
  width?: number;
  /** Keep the side column when not `open` (view mode) — see above. The sheet still needs
   *  `open`. */
  dockInView?: boolean;
}

export function LedgerDock({
  open,
  ledger,
  children,
  stickyTop = 56,
  viewportOffset = 0,
  layout = 'auto',
  width: sideWidth = LEDGER_WIDTH,
  dockInView = false,
}: LedgerDockProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox((b) => (b.left === r.left && b.width === r.width ? b : { left: r.left, width: r.width }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Unmeasured (0) counts as wide: the side column is the designed default.
  const { width } = box;
  const variant: LedgerVariant =
    layout !== 'auto' ? layout : width > 0 && width < SHEET_BELOW_PX ? 'sheet' : 'side';
  const side = (open || dockInView) && variant === 'side';
  const sheet = open && variant === 'sheet';
  const shown = side || sheet;
  // Slide only when the column appears BECAUSE of `open` (E), decided once per appearance.
  const slideRef = useRef<boolean | null>(null);
  if (!shown) slideRef.current = null;
  else if (slideRef.current === null) slideRef.current = open;
  const slide = slideRef.current === true;

  // The side column's height follows its top in the viewport: resting under the header
  // and the spine at the top of the page, then stuck. Written to a custom property, so a
  // scroll never re-renders the ledger.
  const slotRef = useRef<HTMLDivElement>(null);
  const stuckTop = viewportOffset + stickyTop;
  useLayoutEffect(() => {
    const el = slotRef.current;
    if (!side || !el) return undefined;
    let frame = 0;
    const fit = () => {
      frame = 0;
      const top = Math.max(stuckTop, el.getBoundingClientRect().top);
      el.style.setProperty(SIDE_TOP_VAR, `${Math.round(top)}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(fit);
    };
    fit();
    // Capture: the page scrolls inside the AppShell's container, not the window.
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    const ro = new ResizeObserver(schedule);
    if (ref.current) ro.observe(ref.current);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      ro.disconnect();
    };
  }, [side, stuckTop]);
  // The sheet covers the scroller's bottom edge: focus scrolling stops above it, and the
  // page's end scrolls clear of it.
  useBottomBarClearance(slotRef, sheet);

  return (
    <Box
      ref={ref}
      data-ledger-dock={shown ? variant : 'closed'}
      data-ledger-width={sideWidth}
      sx={{
        display: 'grid',
        gridTemplateColumns: side ? `minmax(0, 1fr) ${sideWidth}px` : 'minmax(0, 1fr)',
        columnGap: `${LEDGER_GAP_PX}px`,
        alignItems: 'start',
      }}
    >
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      {shown && (
        <Box
          ref={slotRef}
          data-ledger-slot={variant}
          data-ledger-slide={slide ? 'in' : 'none'}
          sx={
            side
              ? {
                  position: 'sticky',
                  top: stickyTop,
                  // Unmeasured: the stuck top.
                  maxHeight: `calc(100vh - var(${SIDE_TOP_VAR}, ${stuckTop}px) - ${SIDE_BOTTOM_GAP_PX}px)`,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  animationName: slide ? `${slideIn}` : 'none',
                  ...MOTION,
                }
              : {
                  position: 'fixed',
                  bottom: 0,
                  // Over the content column (the dock's box); unmeasured: full width.
                  ...(width > 0 ? { left: box.left, width } : { left: 0, right: 0 }),
                  zIndex: (t) => t.zIndex.appBar - 1,
                  animationName: `${slideUp}`,
                  ...MOTION,
                }
          }
        >
          {ledger(variant)}
        </Box>
      )}
    </Box>
  );
}
